// Reports catalogue. Every card opens a read-only report view; cards with a
// confirmed workbook endpoint also export the default range directly. Input
// input surfaces such as budgets live in Settings, and extension imports live
// in the extension's own workspace, not here.
//
// Every card shows the standard accounting term as its heading and the
// plain-language explanation as visible text — never a tooltip-only title —
// and the search box filters over both, so mobile readers get the same
// vocabulary as a desktop hover.
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  downloadAgedPayablesXlsx,
  downloadAgedReceivablesXlsx,
  downloadBalanceSheetXlsx,
  downloadCashFlowXlsx,
  downloadGlQueryXlsx,
  downloadProfitAndLossXlsx,
  downloadTrialBalanceXlsx,
} from "@/lib/api";

interface ReportCard {
  to: string;
  /** Standard accounting term, shown as the card heading. */
  name: string;
  /** Short plain-language explanation, visible under the heading. */
  summary: string;
  /** One further sentence of plain-language detail. */
  body: string;
  /** Exports the report's current default range as a workbook. */
  exportXlsx?: () => Promise<void>;
}

interface ReportSection {
  label: string;
  cards: ReportCard[];
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function yearStartIso(): string {
  return `${new Date().getFullYear()}-01-01`;
}

const REPORT_SECTIONS: ReportSection[] = [
  {
    label: "Start here",
    cards: [
      {
        to: "/profit-and-loss",
        name: "Profit & loss",
        summary: "Money in vs money out",
        body: "What you earned minus what you spent over a period.",
        exportXlsx: () =>
          downloadProfitAndLossXlsx({
            start_date: firstOfMonthIso(),
            end_date: todayIso(),
            basis: "accrual",
          }),
      },
      {
        to: "/balance-sheet",
        name: "Balance sheet",
        summary: "What you own and owe",
        body: "Everything the business owns and owes on a chosen date.",
        exportXlsx: () => downloadBalanceSheetXlsx(),
      },
      {
        to: "/trial-balance",
        name: "Trial balance",
        summary: "Account balances check",
        body: "Every account's balance, with debits checked against credits.",
        exportXlsx: () => downloadTrialBalanceXlsx(),
      },
      {
        to: "/cash-flow",
        name: "Cash-flow statement",
        summary: "Where your cash went",
        body: "Cash in and out from trading, buying and selling things, and funding.",
        exportXlsx: () =>
          downloadCashFlowXlsx({
            start_date: yearStartIso(),
            end_date: todayIso(),
            method: "indirect",
            basis: "accrual",
          }),
      },
      {
        to: "/aged-receivables",
        name: "Aged receivables",
        summary: "Who owes you",
        body: "Open customer invoices bucketed by how overdue they are.",
        exportXlsx: () => downloadAgedReceivablesXlsx(),
      },
      {
        to: "/aged-payables",
        name: "Aged payables",
        summary: "What you owe",
        body: "Open supplier bills bucketed by how overdue they are.",
        exportXlsx: () => downloadAgedPayablesXlsx(),
      },
      {
        to: "/gl-query",
        name: "General ledger query",
        summary: "Ledger search",
        body: "Every account's movement for a date range, filterable by type.",
        exportXlsx: () =>
          downloadGlQueryXlsx({
            start_date: firstOfMonthIso(),
            end_date: todayIso(),
            basis: "accrual",
          }),
      },
    ],
  },
  {
    label: "Analysis",
    cards: [
      {
        to: "/reports/budget-vs-actual",
        name: "Budget vs actual",
        summary: "Plan vs actual spend",
        body: "What you planned to spend next to what you actually spent.",
      },
      {
        to: "/reports/pnl-comparison",
        name: "Profit & loss comparison",
        summary: "This period vs another",
        body: "Compare income and spending across two periods.",
      },
      {
        to: "/reports/executive-summary",
        name: "Executive summary",
        summary: "The month at a glance",
        body: "Key income, spending, and balance figures for the current month and year.",
      },
      {
        to: "/reports/analytic",
        name: "Costs by location",
        summary: "Spending by site",
        body: "See spending grouped by location.",
      },
      {
        to: "/reports/job-profitability",
        name: "Work profitability",
        summary: "Profit by job",
        body: "Income and costs grouped by job or work item.",
      },
    ],
  },
  {
    label: "Compliance & activity",
    cards: [
      {
        to: "/reports/contractor-payments",
        name: "Contractor payments",
        summary: "Yearly 1099 supplier totals",
        body: "Supplier payments for the year behind a 1099 filing.",
      },
      {
        to: "/reports/cultivation-close",
        name: "Cultivation close",
        summary: "Locked cultivation schedules",
        body: "Plant, harvest, cost, and excise schedules from a locked close, with the workbook download.",
      },
      {
        to: "/audit-log",
        name: "Audit log",
        summary: "Who changed what",
        body: "A filterable history of accounting activity.",
      },
    ],
  },
];

function matchesQuery(card: ReportCard, query: string): boolean {
  if (query.length === 0) return true;
  return `${card.name} ${card.summary} ${card.body}`
    .toLowerCase()
    .includes(query);
}

function ReportCardView({ card }: { card: ReportCard }) {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportXlsx = async () => {
    if (!card.exportXlsx) return;
    setExporting(true);
    setError(null);
    try {
      await card.exportXlsx();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="relative h-full">
      <Link
        to={card.to}
        aria-label={`Open ${card.name}`}
        className="absolute inset-0 z-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]"
      >
        <span className="sr-only">Open {card.name}</span>
      </Link>
      <CardContent className="pointer-events-none relative z-10 grid h-full gap-1 p-5">
        <CardTitle>{card.name}</CardTitle>
        <CardDescription>{card.summary}</CardDescription>
        <p className="text-xs text-[var(--muted-foreground)]">{card.body}</p>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-medium text-[var(--muted-foreground)]">
            View report →
          </span>
          {card.exportXlsx ? (
            <span className="pointer-events-auto">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={exportXlsx}
                disabled={exporting}
                aria-label={`Export ${card.name} workbook`}
              >
                {exporting ? "Exporting..." : "Export XLSX"}
              </Button>
            </span>
          ) : null}
        </div>
        {error ? (
          <p role="status" className="text-xs text-[var(--danger)]">
            {error}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function ReportsPage() {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const sections = REPORT_SECTIONS.map((section) => ({
    ...section,
    cards: section.cards.filter((card) =>
      matchesQuery(card, normalizedQuery),
    ),
  })).filter((section) => section.cards.length > 0);
  const matchCount = sections.reduce(
    (count, section) => count + section.cards.length,
    0,
  );

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Reports"
        description="Open a report to review it, or export its default workbook directly."
      />

      <div className="grid gap-1">
        <label htmlFor="reports-search" className="text-xs font-medium">
          Search reports
        </label>
        <Input
          id="reports-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Report name or plain-language explanation"
          autoComplete="off"
        />
      </div>

      {sections.map((section) => (
        <section
          key={section.label}
          aria-label={section.label}
          className="grid gap-3"
        >
          <h2 className="text-lg font-semibold tracking-tight">
            {section.label}
          </h2>
          <ul className="grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
            {section.cards.map((card) => (
              <li key={card.to} className="list-none">
                <ReportCardView card={card} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {matchCount === 0 ? (
        <p
          role="status"
          className="text-sm text-[var(--muted-foreground)]"
        >
          No reports match “{query.trim()}”.
        </p>
      ) : null}
    </div>
  );
}
